import type { UseFormReturn } from 'react-hook-form';

import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { EditorRefHandle } from '~/components';

import { RichTextEditor, Separator, Title } from '~/components';
import { Field } from '~/components/Form';
import { FormBrand, FormClient, FormTitle } from '~/features/documents';
import { mergeRefs } from '~/utils/mergeRefs';

import type { CreateEditQuotationFormType } from '../types';

import { FormCoEntryNumber } from '../../components/FormCoEntryNumber';
import { FormExpirationDate } from '../../components/FormExpirationDate';
import { columnStyle, rowStyle, separatorStyle } from './QuotationBasicSection.css';

type Props = {
  form: UseFormReturn<CreateEditQuotationFormType>;
  previousEntryNumber: string | null;
};

export const QuotationBasicSection = ({ form, previousEntryNumber }: Props) => {
  const { t } = useTranslation(['invoices', 'quotations', 'validation']);

  const { control } = form;

  const introductionEditorRef = useRef<EditorRefHandle>(null);
  const descriptionEditorRef = useRef<EditorRefHandle>(null);

  return (
    <>
      <Title as="h2" styleAs="h3">
        {t('quotations:createEdit.basicInfo.title')}
      </Title>

      <div className={rowStyle}>
        {/* Column 1 — title, introduction, description & client */}
        <div className={columnStyle}>
          <FormTitle form={form} />

          {/* Introduction */}
          <Controller
            control={control}
            name="introduction"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label>{t('quotations:fields.introduction.label')}</Field.Label>

                <RichTextEditor
                  isInvalid={invalid}
                  mode="html"
                  onBlur={onBlur}
                  onChange={onChange}
                  ref={mergeRefs(ref, introductionEditorRef)}
                  value={value}
                />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: { notEmpty: () => !introductionEditorRef.current?.isEmpty() || t('validation:required') },
            }}
          />

          {/* Description */}
          <Controller
            control={control}
            name="description"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label>{t('quotations:fields.description.label')}</Field.Label>

                <RichTextEditor
                  isInvalid={invalid}
                  mode="html"
                  onBlur={onBlur}
                  onChange={onChange}
                  ref={mergeRefs(ref, descriptionEditorRef)}
                  value={value}
                />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: { notEmpty: () => !descriptionEditorRef.current?.isEmpty() || t('validation:required') },
            }}
          />

          {/* Client */}
          <FormClient domain="quotations" form={form} />
        </div>

        <Separator className={separatorStyle} orientation="vertical" />

        {/* Column 2 — entry number, expiration date & brand */}
        <div className={columnStyle}>
          <FormCoEntryNumber form={form} previousEntryNumber={previousEntryNumber} />
          <FormExpirationDate form={form} />
          <FormBrand form={form} />
        </div>
      </div>
    </>
  );
};
